import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

/* ── Theme ─────────────────────────────────────────────────── */
const GOLD       = '#C9952A';
const GOLD_LIGHT = '#E8B84B';
const CREAM      = '#FAF6ED';
const WARM_WHITE = '#FFFCF5';
const TEXT       = '#2A1F0E';
const TEXT_MID   = '#6B5540';

const LAST_UPDATED = 'June 24, 2026';

const shimmer = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-8px); }
  100% { transform: translateY(0px); }
`;

/* ── Layout ─────────────────────────────────────────────────── */
const PageContainer = styled.div`
  background: ${CREAM};
  overflow: hidden;
`;

const Hero = styled.section`
  position: relative;
  min-height: 52vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5rem 2rem 8rem;
  background: linear-gradient(135deg, #1A1208 0%, #2A1A08 55%, #1A1208 100%);
  overflow: hidden;
  clip-path: polygon(0 0, 100% 0, 100% 88%, 0 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(ellipse at 50% -10%, rgba(201,149,42,0.28) 0%, transparent 55%),
      radial-gradient(ellipse at 5%  90%,  rgba(201,149,42,0.10) 0%, transparent 40%),
      radial-gradient(ellipse at 95% 90%,  rgba(201,149,42,0.10) 0%, transparent 40%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='80' height='80' xmlns='http://www.w3.org/2000/svg'%3E%3Cellipse cx='40' cy='12' rx='8' ry='18' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(0%2C40%2C40)'/%3E%3Cellipse cx='40' cy='12' rx='8' ry='18' fill='rgba(201%2C149%2C42%2C0.04)' transform='rotate(90%2C40%2C40)'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.8rem, 5.5vw, 4.5rem);
  font-weight: 900;
  margin-bottom: 0.8rem;
  line-height: 1.05;
  background: linear-gradient(135deg, #fff 0%, ${GOLD_LIGHT} 50%, #fff 100%);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: ${shimmer} 6s ease infinite, ${float} 7s ease-in-out infinite;
`;

const HeroSub = styled.p`
  font-size: 1rem;
  color: rgba(255,255,255,0.55);
  font-family: 'Source Serif 4', serif;
  letter-spacing: 0.03em;
`;

const GoldRule = styled.div`
  width: 60px;
  height: 1.5px;
  background: linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT});
  margin: 1.2rem auto;
  border-radius: 2px;
`;

const MainContent = styled.div`
  max-width: 840px;
  margin: -4rem auto 0;
  padding: 0 2rem 6rem;
  position: relative;
  z-index: 2;
`;

/* ── Card ───────────────────────────────────────────────────── */
const Card = styled.section`
  background: ${WARM_WHITE};
  border-radius: 20px;
  padding: 2.8rem 3rem;
  margin-bottom: 1.6rem;
  box-shadow: 0 8px 28px rgba(201,149,42,0.09);
  border: 1px solid rgba(201,149,42,0.12);

  @media (max-width: 600px) {
    padding: 2rem 1.6rem;
  }
`;

const IntroCard = styled(Card)`
  background: linear-gradient(135deg, rgba(201,149,42,0.06), rgba(232,184,75,0.04));
  border-color: rgba(201,149,42,0.22);
  text-align: center;
`;

/* ── Typography ─────────────────────────────────────────────── */
const SectionTitle = styled.h2`
  font-family: 'Playfair Display', serif;
  font-size: 1.45rem;
  font-weight: 700;
  color: ${TEXT};
  margin-bottom: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.7rem;

  span.num {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT});
    color: #fff;
    font-size: 0.72rem;
    font-family: 'Source Serif 4', serif;
    font-weight: 700;
    flex-shrink: 0;
  }
`;

const Para = styled.p`
  font-size: 0.97rem;
  line-height: 1.95;
  color: ${TEXT_MID};
  font-family: 'Source Serif 4', serif;
  margin-bottom: 0.9rem;

  &:last-child { margin-bottom: 0; }
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0.9rem;

  li {
    font-size: 0.97rem;
    line-height: 1.8;
    color: ${TEXT_MID};
    font-family: 'Source Serif 4', serif;
    padding-left: 1.4rem;
    position: relative;
    margin-bottom: 0.3rem;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0.6em;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: ${GOLD};
    }
  }
`;

const Highlight = styled.strong`
  color: ${TEXT};
  font-weight: 600;
`;

const ContactLink = styled.a`
  color: ${GOLD};
  font-weight: 600;
  text-decoration: none;
  &:hover { color: #9A6F1A; text-decoration: underline; }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(201,149,42,0.15);
  margin: 1.6rem 0;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${GOLD};
  font-size: 0.9rem;
  font-family: 'Source Serif 4', serif;
  font-weight: 600;
  text-decoration: none;
  margin-bottom: 1.5rem;

  &:hover { color: #9A6F1A; text-decoration: underline; }
`;

/* ── Component ──────────────────────────────────────────────── */
const PrivacyPolicy = () => (
  <PageContainer>
    <Hero>
      <HeroContent>
        <HeroTitle>Privacy Policy</HeroTitle>
        <GoldRule />
        <HeroSub>Last updated: {LAST_UPDATED}</HeroSub>
      </HeroContent>
    </Hero>

    <MainContent>
      <BackLink to="/contact">← Contact Us</BackLink>

      {/* ── INTRO ─────────────────────────────────────────── */}
      <IntroCard>
        <Para>
          <Highlight>BHERI Technology Foundation</Highlight> ("we", "our", or "us") is committed to
          protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard
          information when you use our website <Highlight>bheri.in</Highlight>, mobile applications,
          and associated services (collectively, the "Services").
        </Para>
        <Para>
          By using our Services, you agree to the practices described in this policy. If you do not
          agree, please discontinue use of our Services.
        </Para>
      </IntroCard>

      {/* 1 */}
      <Card>
        <SectionTitle><span className="num">1</span> Information We Collect</SectionTitle>
        <Para>We may collect the following types of information:</Para>

        <Para><Highlight>Information you provide directly:</Highlight></Para>
        <BulletList>
          <li>Name and email address when you contact us</li>
          <li>Queries or feedback submitted through contact forms</li>
          <li>Any other information you voluntarily share with us</li>
        </BulletList>

        <Para><Highlight>Information collected automatically:</Highlight></Para>
        <BulletList>
          <li>Browser type, operating system, and device information</li>
          <li>IP address and approximate geographic location</li>
          <li>Pages visited, time spent on pages, and navigation paths</li>
          <li>Referring URLs and search terms used to reach our site</li>
        </BulletList>

        <Para><Highlight>Information from mobile apps (Dhara):</Highlight></Para>
        <BulletList>
          <li>Search queries and content preferences within the app</li>
          <li>App usage analytics and crash reports</li>
          <li>Device identifiers for app performance and analytics</li>
        </BulletList>
      </Card>

      {/* 2 */}
      <Card>
        <SectionTitle><span className="num">2</span> How We Use Your Information</SectionTitle>
        <Para>We use the information we collect to:</Para>
        <BulletList>
          <li>Provide, maintain, and improve our Services</li>
          <li>Respond to your queries, comments, and requests</li>
          <li>Analyse usage patterns to enhance user experience</li>
          <li>Monitor and ensure the security of our Services</li>
          <li>Comply with applicable legal obligations</li>
          <li>Send service-related communications (not marketing, unless you opt in)</li>
        </BulletList>
        <Para>
          We do <Highlight>not</Highlight> sell, rent, or trade your personal information to third
          parties for marketing purposes.
        </Para>
      </Card>

      {/* 3 */}
      <Card>
        <SectionTitle><span className="num">3</span> Cookies &amp; Tracking Technologies</SectionTitle>
        <Para>
          Our website may use cookies and similar tracking technologies to enhance your browsing
          experience. Cookies are small data files stored on your device.
        </Para>
        <BulletList>
          <li><Highlight>Essential cookies:</Highlight> Required for the website to function correctly</li>
          <li><Highlight>Analytics cookies:</Highlight> Help us understand how visitors interact with our site (e.g., Google Analytics)</li>
          <li><Highlight>Preference cookies:</Highlight> Remember your settings and preferences</li>
        </BulletList>
        <Para>
          You can control cookies through your browser settings. Disabling essential cookies may
          affect the functionality of certain parts of our Services.
        </Para>
      </Card>

      {/* 4 */}
      <Card>
        <SectionTitle><span className="num">4</span> Sharing of Information</SectionTitle>
        <Para>We may share your information only in the following circumstances:</Para>
        <BulletList>
          <li>
            <Highlight>Service providers:</Highlight> Trusted third-party vendors who assist us in
            operating our Services (e.g., hosting, analytics), bound by confidentiality obligations
          </li>
          <li>
            <Highlight>Legal requirements:</Highlight> When required by law, court order, or
            governmental authority
          </li>
          <li>
            <Highlight>Protection of rights:</Highlight> To protect the rights, property, or safety
            of BHERI Technology Foundation, our users, or the public
          </li>
          <li>
            <Highlight>Business transfers:</Highlight> In connection with a merger, acquisition, or
            sale of assets, with notice provided to affected users
          </li>
        </BulletList>
      </Card>

      {/* 5 */}
      <Card>
        <SectionTitle><span className="num">5</span> Data Security</SectionTitle>
        <Para>
          We implement industry-standard technical and organisational measures to protect your
          information against unauthorised access, alteration, disclosure, or destruction. All data
          transmitted between your device and our servers is encrypted in transit.
        </Para>
        <Para>
          However, no method of transmission over the internet or electronic storage is completely
          secure. While we strive to use commercially acceptable means to protect your data, we
          cannot guarantee absolute security.
        </Para>
      </Card>

      {/* 6 */}
      <Card>
        <SectionTitle><span className="num">6</span> Data Retention</SectionTitle>
        <Para>
          We retain personal information only for as long as necessary to fulfil the purposes
          described in this policy, or as required by applicable law. When data is no longer needed,
          we securely delete or anonymise it.
        </Para>
        <Para>
          You may request deletion of your personal data at any time by contacting us at{' '}
          <ContactLink href="mailto:namaste@bheri.in">namaste@bheri.in</ContactLink>.
        </Para>
      </Card>

      {/* 7 */}
      <Card>
        <SectionTitle><span className="num">7</span> Your Rights</SectionTitle>
        <Para>Depending on your jurisdiction, you may have the right to:</Para>
        <BulletList>
          <li>Access the personal information we hold about you</li>
          <li>Request correction of inaccurate or incomplete data</li>
          <li>Request deletion of your personal data</li>
          <li>Object to or restrict our processing of your data</li>
          <li>Withdraw consent at any time (where processing is based on consent)</li>
          <li>Lodge a complaint with a supervisory authority</li>
        </BulletList>
        <Para>
          To exercise any of these rights, please contact us at{' '}
          <ContactLink href="mailto:namaste@bheri.in">namaste@bheri.in</ContactLink>. We will
          respond within a reasonable timeframe and in accordance with applicable law.
        </Para>
      </Card>

      {/* 8 */}
      <Card>
        <SectionTitle><span className="num">8</span> Third-Party Links</SectionTitle>
        <Para>
          Our Services may contain links to third-party websites or services (e.g., Google Play
          Store, external research repositories). We are not responsible for the privacy practices
          of these third parties. We encourage you to review their respective privacy policies
          before providing any personal information.
        </Para>
      </Card>

      {/* 9 */}
      <Card>
        <SectionTitle><span className="num">9</span> Children's Privacy</SectionTitle>
        <Para>
          Our Services are not directed to children under the age of 13. We do not knowingly collect
          personal information from children under 13. If you believe we have inadvertently collected
          such information, please contact us immediately and we will take steps to delete it.
        </Para>
      </Card>

      {/* 10 */}
      <Card>
        <SectionTitle><span className="num">10</span> Changes to This Policy</SectionTitle>
        <Para>
          We may update this Privacy Policy from time to time to reflect changes in our practices or
          applicable law. When we make material changes, we will update the "Last updated" date at
          the top of this page and, where appropriate, notify users through our Services or by email.
        </Para>
        <Para>
          We encourage you to review this policy periodically to stay informed about how we protect
          your information.
        </Para>
      </Card>

      {/* 11 — Contact */}
      <Card>
        <SectionTitle><span className="num">11</span> Contact Us</SectionTitle>
        <Para>
          If you have any questions, concerns, or requests regarding this Privacy Policy or our data
          practices, please reach out to us:
        </Para>
        <Divider />
        <Para>
          <Highlight>BHERI Technology Foundation</Highlight><br />
          Indian Institute of Technology Hyderabad<br />
          Kandi, Sangareddy, Telangana — 502285, India
        </Para>
        <Para>
          Email: <ContactLink href="mailto:namaste@bheri.in">namaste@bheri.in</ContactLink>
        </Para>
      </Card>
    </MainContent>
  </PageContainer>
);

export default PrivacyPolicy;
